import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Alert } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import api from "../api/api";

export default function ListaAsistencia() {
  const { idCurso } = useParams();
  const [searchParams] = useSearchParams();
  const codigoFicha = searchParams.get("ficha") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [config, setConfig] = useState({ company_name: "", attendance_doc_code: "", attendance_doc_rev: "" });

  useEffect(() => {
    if (!idCurso) {
      setError("Invalid parameters: missing id_curso.");
      setLoading(false);
      return;
    }
    const params = codigoFicha ? { ficha: codigoFicha } : {};
    api
      .get(`/fichas/lista-asistencia/${idCurso}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail ?? "Error loading data"))
      .finally(() => setLoading(false));
  }, [idCurso, codigoFicha]);

  useEffect(() => {
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }
  if (!data) return null;

  const hoy = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  const emptyRows = Math.max(5, 20 - data.empleados.length);

  const cellStyle = (extra = {}) => ({
    border: "1px solid black",
    padding: "3px 7px",
    fontSize: 11,
    ...extra,
  });

  return (
    <>
      {/* Control bar — hidden when printing */}
      <Box
        className="no-print"
        sx={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          bgcolor: "#1565c0", color: "white",
          p: 1.5, display: "flex", justifyContent: "center", gap: 2, alignItems: "center",
          boxShadow: 3,
        }}
      >
        <Button
          variant="contained"
          sx={{ bgcolor: "white", color: "#1565c0", "&:hover": { bgcolor: "#e3f2fd" }, fontWeight: 700 }}
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
        >
          Print / Save PDF
        </Button>
        <Button
          variant="outlined"
          sx={{ borderColor: "rgba(255,255,255,0.7)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
          startIcon={<CloseIcon />}
          onClick={() => window.close()}
        >
          Close
        </Button>
      </Box>

      {/* Print content */}
      <div
        id="lista-print-wrapper"
        style={{ padding: "72px 24px 24px", maxWidth: 920, margin: "0 auto", fontFamily: "Arial, Helvetica, sans-serif" }}
      >
        {/* ── Header ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", border: "2px solid black", marginBottom: 6 }}>
          <tbody>
            <tr>
              {config.company_name && (
                <td style={{ ...cellStyle({ width: 130, textAlign: "center", verticalAlign: "middle", borderRight: "2px solid black", padding: "8px" }) }}>
                  <div style={{ fontWeight: "bold", fontSize: 15, lineHeight: 1.3 }}>{config.company_name}</div>
                </td>
              )}
              <td style={{ ...cellStyle({ textAlign: "center", verticalAlign: "middle", padding: "8px" }) }}>
                <div style={{ fontWeight: "bold", fontSize: 20, textTransform: "uppercase", letterSpacing: 1 }}>
                  Attendance Sheet
                </div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                  Training / Instruction
                </div>
              </td>
              <td style={{ ...cellStyle({ width: 185, verticalAlign: "top", borderLeft: "2px solid black", padding: "7px 10px", fontSize: 11 }) }}>
                {config.attendance_doc_code && <div><strong>Code:</strong> {config.attendance_doc_code}</div>}
                {config.attendance_doc_rev && <div style={{ marginTop: 2 }}><strong>Rev.:</strong> {config.attendance_doc_rev}</div>}
                <div style={{ marginTop: 2 }}><strong>Issue date:</strong> {hoy}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── Course and working sheet data ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid black", marginBottom: 6 }}>
          <tbody>
            {data.ficha && (
              <tr>
                <td style={{ ...cellStyle({ width: 150, fontWeight: "bold" }) }}>WORKING SHEET / CODE:</td>
                <td style={{ ...cellStyle({ width: 170 }) }}>
                  {data.ficha.codigo}
                  {data.ficha.version && (
                    <span style={{ marginLeft: 8, fontSize: 10, color: "#444" }}>Rev. {data.ficha.version}</span>
                  )}
                </td>
                <td style={{ ...cellStyle({ width: 180, fontWeight: "bold" }) }}>INSTRUCTION NAME:</td>
                <td style={{ ...cellStyle() }}>{data.ficha.nombre}</td>
              </tr>
            )}
            <tr>
              <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>COURSE / TRAINING:</td>
              <td colSpan={3} style={{ ...cellStyle({ fontWeight: "500" }) }}>{data.curso.nombre_curso}</td>
            </tr>
            <tr>
              <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>VERSION:</td>
              <td style={{ ...cellStyle() }}>{data.curso.numero_version ?? "—"}</td>
              <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>DURATION:</td>
              <td style={{ ...cellStyle() }}>
                {data.curso.duracion_horas ? `${data.curso.duracion_horas} hrs` : "—"}
              </td>
            </tr>
            {data.version_fichas?.length > 0 && (
              <tr>
                <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>WORKING SHEETS WITH CHANGES:</td>
                <td colSpan={3} style={{ ...cellStyle() }}>
                  {data.version_fichas.map(f => (
                    <span key={f.codigo} style={{ marginRight: 16, display: "inline-block" }}>
                      <strong>{f.codigo}</strong>
                      {f.version && (
                        <span style={{ color: "#444", marginLeft: 4, fontSize: 10 }}>Rev. {f.version}</span>
                      )}
                      {f.nombre && f.nombre !== f.codigo && (
                        <span style={{ color: "#666", marginLeft: 4 }}>— {f.nombre}</span>
                      )}
                    </span>
                  ))}
                </td>
              </tr>
            )}
            <tr>
              <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>INSTRUCTOR:</td>
              <td style={{ ...cellStyle({ minWidth: 140 }) }}>{data.curso.instructor || " "}</td>
              <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>LOCATION / ROOM:</td>
              <td style={{ ...cellStyle() }}>{data.curso.lugar || " "}</td>
            </tr>
          </tbody>
        </table>

        {/* ── Attendees table ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 20 }}>
          <thead>
            <tr style={{ backgroundColor: "#000", color: "white" }}>
              <th style={{ border: "1px solid black", padding: "5px 6px", width: 36, textAlign: "center" }}>No.</th>
              <th style={{ border: "1px solid black", padding: "5px 8px", width: 90, textAlign: "center" }}>PAYROLL #</th>
              <th style={{ border: "1px solid black", padding: "5px 10px", textAlign: "left" }}>FULL NAME</th>
              <th style={{ border: "1px solid black", padding: "5px 8px", width: 150, textAlign: "left" }}>POSITION</th>
              <th style={{ border: "1px solid black", padding: "5px 8px", width: 100, textAlign: "left" }}>AREA</th>
              <th style={{ border: "1px solid black", padding: "5px 8px", width: 120, textAlign: "center" }}>SIGNATURE</th>
            </tr>
          </thead>
          <tbody>
            {data.empleados.map((emp, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                <td style={{ border: "1px solid black", padding: "3px 6px", textAlign: "center" }}>{i + 1}</td>
                <td style={{ border: "1px solid black", padding: "3px 8px", textAlign: "center", fontSize: 10 }}>{emp.numero_nomina ?? "—"}</td>
                <td style={{ border: "1px solid black", padding: "3px 8px" }}>{emp.nombre_completo}</td>
                <td style={{ border: "1px solid black", padding: "3px 8px", fontSize: 10 }}>{emp.puesto_texto}</td>
                <td style={{ border: "1px solid black", padding: "3px 8px", fontSize: 10 }}>{emp.area_texto}</td>
                <td style={{ border: "1px solid black", padding: "3px 8px" }}>&nbsp;</td>
              </tr>
            ))}
            {/* Additional empty rows */}
            {Array.from({ length: emptyRows }).map((_, i) => (
              <tr key={`empty-${i}`} style={{ height: 22 }}>
                <td style={{ border: "1px solid black", padding: "2px 6px", textAlign: "center", color: "#ccc", fontSize: 10 }}>
                  {data.empleados.length + i + 1}
                </td>
                <td style={{ border: "1px solid black" }}>&nbsp;</td>
                <td style={{ border: "1px solid black" }}>&nbsp;</td>
                <td style={{ border: "1px solid black" }}>&nbsp;</td>
                <td style={{ border: "1px solid black" }}>&nbsp;</td>
                <td style={{ border: "1px solid black" }}>&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Closing signatures ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 11 }}>
          <tbody>
            <tr>
              {["PREPARED BY", "REVIEWED BY", "APPROVED BY"].map((label) => (
                <td key={label} style={{ width: "33.3%", textAlign: "center", padding: "0 24px", verticalAlign: "bottom" }}>
                  <div style={{ borderTop: "1px solid black", paddingTop: 4, marginTop: 44 }}>
                    <strong>{label}</strong>
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>

        {/* Total expected attendees indicator */}
        <div style={{ marginTop: 12, fontSize: 10, color: "#777", textAlign: "right" }}>
          Total expected attendees: <strong>{data.empleados.length}</strong>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          #lista-print-wrapper { padding: 0 !important; max-width: 100% !important; }
          @page { margin: 10mm; size: A4 portrait; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; }
        }
      `}</style>
    </>
  );
}
